import React, { useState, useEffect, useCallback } from 'react';
import { Card, Steps, Tag, Typography, Space, Tooltip, Alert, Button } from 'antd';
import {
  InfoCircleOutlined,
  CheckCircleFilled,
  StopFilled,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { adminApi } from '@/services/api';

const { Text, Paragraph } = Typography;

/** The server-side switch that turns per-build resolution on and off. */
const RESOLUTION_FLAG = 'build_config_resolution_enabled';

/**
 * Modules whose content can be limited to specific app versions. Kept here so
 * the list an admin reads is one thing to update, and so "where can I actually
 * use this?" has an answer on the page rather than in a document.
 */
const SCOPED_MODULES = [
  'Daily deals',
  'Hot events',
  'Offers',
  'Products',
  'Missions',
  'Deeplinks',
  'Custom ads',
  'Branding campaigns',
  'Inbox messages',
  'Live events',
  'Matchday director',
];

/**
 * Scoped on the server but with no editor in this panel yet: the read path
 * resolves a per-version row correctly, but nothing here can create one, so in
 * practice these are shared by every build. Listed separately and honestly —
 * putting them above would send someone looking for a picker that is not there.
 */
const SCOPED_NO_EDITOR = ['Season pass', 'Daily challenges', 'Login & stump rewards'];

/**
 * Gated by build NUMBER rather than by config version. Kept separate because
 * the admin sets a min/max build range on these, not a Config Version picker —
 * telling someone to look for the wrong control is worse than saying nothing.
 */
const BUILD_GATED_MODULES = [
  'Player masters',
  'Products',
  'Offers',
  'Deeplinks',
  'Hot events',
  'Inbox messages',
  'Live events',
  'Missions',
];

/** Things that are deliberately the same for every build, and why. */
const GLOBAL_ONLY: Array<{ what: string; why: string }> = [
  { what: 'Maintenance mode', why: 'Taking the game down is fleet-wide by nature.' },
  {
    what: 'Fan Rating curve',
    why: 'Two players in one match must be rated by the same formula.',
  },
  { what: 'Blitz tunables', why: 'Same reason as Fan Rating — shared match maths.' },
  {
    what: 'Server feature flags',
    why: 'Flags like iap_enabled gate whole endpoints. Only client feature.* toggles can differ per build.',
  },
  {
    what: 'Custom match teams',
    why: 'Served as one prebuilt payload for the whole fleet, so the squad list is the same for every build.',
  },
];

interface PerBuildConfigGuideProps {
  /** Which tab is showing it, so the current step is highlighted. */
  current: 'config-versions' | 'app-versions';
  /** Counts from the tab, so the steps can show real progress. */
  configVersionCount?: number;
  appVersionCount?: number;
}

/**
 * The "what am I looking at" panel shown above both per-build config tabs.
 *
 * Per-build config spans two tabs and a field on ten other pages, so the
 * relationship between them is the thing most likely to be misunderstood.
 * The steps are a genuine sequence — a config version does nothing until an
 * app version points at it, and neither does anything until content is scoped.
 */
export const PerBuildConfigGuide: React.FC<PerBuildConfigGuideProps> = ({
  current,
  configVersionCount,
  appVersionCount,
}) => {
  const [resolutionEnabled, setResolutionEnabled] = useState<boolean | null>(null);
  const [open, setOpen] = useState(false);

  const loadFlag = useCallback(async () => {
    try {
      const res = await adminApi.getFeatureFlags();
      const flags = (res.data ?? {}) as Record<string, boolean>;
      // Absent flag means enabled: the server treats it as fail-open.
      setResolutionEnabled(flags[RESOLUTION_FLAG] ?? true);
    } catch {
      setResolutionEnabled(null);
    }
  }, []);

  useEffect(() => {
    void loadFlag();
  }, [loadFlag]);

  const stepIndex = current === 'config-versions' ? 0 : 1;

  return (
    <Card size="small" style={{ marginBottom: 16 }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space wrap size="small">
          <Text strong>
            <InfoCircleOutlined /> Per-build configuration
          </Text>

          {resolutionEnabled === false && (
            <Tooltip title="The build_config_resolution_enabled flag is off, so every client is being served the global configuration and the mappings below are ignored. Turn it back on from the Feature Flags tab.">
              <Tag icon={<StopFilled />} color="error">
                Resolution OFF — mappings ignored
              </Tag>
            </Tooltip>
          )}
          {resolutionEnabled === true && (
            <Tooltip title="Clients that send a version header receive the config version their build is mapped to.">
              <Tag icon={<CheckCircleFilled />} color="success">
                Resolution active
              </Tag>
            </Tooltip>
          )}

          <Tooltip title="Anything you leave as shared keeps going to every build, so nothing changes until you deliberately scope it.">
            <Tag color="default">Safe by default</Tag>
          </Tooltip>

          <Tooltip title="Clients pick up config on their next launch (or when they return to the main menu). Nothing changes mid-session.">
            <Tag color="default">Applies on next app launch</Tag>
          </Tooltip>

          <Button
            type="link"
            size="small"
            icon={<QuestionCircleOutlined />}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? 'Hide details' : 'What can I scope?'}
          </Button>
        </Space>

        <Steps
          size="small"
          current={stepIndex}
          items={[
            {
              title: 'Create a config version',
              description: (
                <span>
                  A named bundle of settings.
                  {typeof configVersionCount === 'number' && (
                    <>
                      {' '}
                      <Text type="secondary">({configVersionCount} so far)</Text>
                    </>
                  )}
                  <br />
                  <Text type="secondary">On its own it changes nothing.</Text>
                </span>
              ),
            },
            {
              title: 'Map app versions to it',
              description: (
                <span>
                  Tells the server which builds get it.
                  {typeof appVersionCount === 'number' && (
                    <>
                      {' '}
                      <Text type="secondary">({appVersionCount} mapped)</Text>
                    </>
                  )}
                  <br />
                  <Text type="secondary">Unmapped builds keep the global config.</Text>
                </span>
              ),
            },
            {
              title: 'Scope your content',
              description: (
                <span>
                  Set <Text code>Config Version</Text> on a deal, event, offer…
                  <br />
                  <Text type="secondary">Left empty, it goes to every build.</Text>
                </span>
              ),
            },
            {
              title: 'Check a real player',
              description: (
                <span>
                  Player page shows their build and config version.
                  <br />
                  <Text type="secondary">Confirms what they actually received.</Text>
                </span>
              ),
            },
          ]}
        />

        {open && (
          <div
            style={{
              borderTop: '1px solid rgba(0,0,0,0.06)',
              paddingTop: 12,
            }}
          >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div>
                <Paragraph style={{ marginBottom: 6 }}>
                  <Text strong>Config Version picker</Text> — pick a version on the item itself, and
                  only builds mapped to that version see it:
                </Paragraph>
                <Space wrap size={[4, 4]}>
                  {SCOPED_MODULES.map((m) => (
                    <Tag key={m} color="blue">
                      {m}
                    </Tag>
                  ))}
                </Space>
              </div>

              <div>
                <Paragraph style={{ marginBottom: 6 }}>
                  <Text strong>No editor yet</Text> — the server already serves a per-version row
                  for these, but nothing here can create one, so today they are the same for every
                  build:
                </Paragraph>
                <Space wrap size={[4, 4]}>
                  {SCOPED_NO_EDITOR.map((m) => (
                    <Tag key={m}>{m}</Tag>
                  ))}
                </Space>
              </div>

              <div>
                <Paragraph style={{ marginBottom: 6 }}>
                  <Text strong>Min / Max Build Version</Text> — a build-number range on the item,
                  set independently of config versions:
                </Paragraph>
                <Space wrap size={[4, 4]}>
                  {BUILD_GATED_MODULES.map((m) => (
                    <Tag key={m} color="geekblue">
                      {m}
                    </Tag>
                  ))}
                </Space>
                <Paragraph type="secondary" style={{ marginTop: 6, marginBottom: 0 }}>
                  Player masters use this and only this — a card a player already owns looks its
                  master up by index, so hiding one from a build must never change what an existing
                  card resolves to.
                </Paragraph>
              </div>

              <div>
                <Paragraph style={{ marginBottom: 6 }}>
                  <Text strong>Always the same for every build</Text> — these cannot be scoped, on
                  purpose:
                </Paragraph>
                <Space direction="vertical" size={2}>
                  {GLOBAL_ONLY.map(({ what, why }) => (
                    <Text key={what} type="secondary">
                      <Tag>{what}</Tag>
                      {why}
                    </Text>
                  ))}
                </Space>
              </div>

              <Alert
                type="info"
                showIcon
                message="Two ways a build is recognised"
                description={
                  <span>
                    The app reports a <Text code>version</Text> (like 1.0.2) and a{' '}
                    <Text code>build number</Text> (like 152). Record both on a mapping when you can
                    — older clients only send the number, and a build number of 1 is treated as
                    unknown because a missing header reads as 1.
                  </span>
                }
              />
            </Space>
          </div>
        )}
      </Space>
    </Card>
  );
};

export default PerBuildConfigGuide;
